// ─── Il ponte per il bot dei soci — `/api/v1/consumer` (§ 02.7) ──────────────
//
// 🧭 **Il gestionale SA, il bot DICE.** Di qua passa tutto ciò che il bot può
// sapere, e non passa nient'altro: il bot non interroga il database, non tiene
// un archivio suo, non deduce da ciò che ha detto prima.
//
// 🎯 **La prova del futuro, ed è la ragione per cui questo ponte è un contratto
// nuovo invece di una copia dei vecchi percorsi**: *il giorno in cui Matchpoint
// si spegne, il bot non si tocca.* Se una riga del bot dovesse cambiare quel
// giorno, quella riga è già sbagliata adesso. ⇒ Qui dentro non compare **nessun**
// identificativo che nasca di là: né il codice tessera, né `idReserva`, né un
// `matchpointIdInterno`. Si riconosce una persona dal **suo id**, che è nostro.
//
// ⛔ **Fallisce chiuso, e in due modi diversi che non vanno confusi:**
//   · **non esiste nessuna chiave viva** → `503 BRIDGE_DISARMED`: un ponte
//     senza chiavi non è un ponte aperto, è un ponte spento;
//   · **la chiave offerta non apre** → `401`: le chiavi ci sono, non è quella.
// 📌 Chi confondesse i due lascerebbe passare tutti nel giorno in cui l'ultima
//    chiave viene revocata per sbaglio.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
import { verdettoDelPonteSchema } from './avvisi.js';
import { soprannomeSchema } from './soci.js';
import { caparraDelPonteSchema, creditoInScadenzaSchema } from './caparra.js';
import { RUOLI_NEL_BOT } from './preferenze-bot.js';
/**
 * L'intestazione con cui il bot si presenta: `X-Api-Key: pmo_…`.
 *
 * ⚖️ **Non `Authorization: Bearer`**, e non è gusto: quell'intestazione la legge
 * già la guardia delle persone (`chiediGettone`). Due credenziali diverse sulla
 * stessa riga vorrebbero dire una guardia che indovina quale delle due sta
 * guardando — e il giorno in cui indovina male, o fa passare un bot per una
 * persona, o rifiuta una persona perché non è un bot.
 * 📌 *Due cose che non si possono confondere non condividono il posto in cui si
 *    scrivono.*
 */
export const INTESTAZIONE_CHIAVE = 'x-api-key';
/** @deprecated Il segreto unico del ponte, in pensione con le chiavi (22/09/2026). */
export const INTESTAZIONE_PONTE = 'x-bridge-secret';
// ── Chi sei ─────────────────────────────────────────────────────────────────
/**
 * ⚠️ Un'identificazione si chiede **o** con il telefono **o** con l'id nostro,
 * mai con tutti e due a caso: due chiavi che rispondono a due persone diverse
 * sarebbero una domanda senza risposta, e la risposta la sceglierebbe l'ordine
 * degli `if`.
 */
export const domandaIdentitaSchema = z
    .object({
    phone: z.string().trim().min(3).optional(),
    memberId: z.uuid().optional(),
})
    .refine((d) => Boolean(d.phone) !== Boolean(d.memberId), {
    message: 'Serve il telefono oppure l’id del socio, uno solo dei due.',
});
/**
 * Quel poco che il bot deve sapere di una persona.
 *
 * ⛔ Non escono di qui: email, borsellino, livello, note, storico. Il bot
 * racconta le partite, non tiene l'anagrafica — e ciò che non attraversa il
 * ponte non può finire in una chat per sbaglio.
 *
 * ⭐ `canBook` è un **sì/no calcolato qui**, non una regola copiata nel bot: il
 * giorno in cui la regola cambia, cambia in un posto solo. È la stessa forma
 * già scelta per `puo_prenotare` sul sistema di oggi.
 */
export const identitaSchema = z.object({
    memberId: z.uuid(),
    displayName: z.string(),
    /**
     * 🆕 Il nome da solo (24/09/2026): il bot chiama il socio per nome, non per
     * nome e cognome. Si manda com'è nella scheda, e non si ricava tagliando
     * `displayName` al primo spazio: «Maria Grazia» è un nome solo.
     */
    firstName: z.string(),
    /**
     * 🏷️ Il soprannome scelto dal socio, se c'è: il bot lo usa al posto del
     * nome. ⇒ `soprannomeSchema`, e la rotta `players/:memberId/nickname`.
     */
    nickname: z.string().nullable(),
    active: z.boolean(),
    canBook: z.boolean(),
    /** Perché no, quando `canBook` è falso. In italiano, per un socio. */
    reason: z.string().nullable(),
    /**
     * 🔄🆕 **IL LIVELLO, e sopra c'era scritto che non usciva di qui** (22/09).
     *
     * 🗣️ Il documento dei flussi lo chiede in chiaro: *«Livello di gioco — il
     * livello esce come parola, mai come numero […]. Se il socio non ha ancora un
     * livello, il bot lo dice e non ne inventa uno»*. E senza livello il bot deve
     * sapere di dover offrire il test: è la chiave che sblocca prenotare,
     * invitare, aprire una partita.
     * ⚖️ Il vecchio divieto («non escono di qui: email, borsellino, livello…»)
     * nasceva da *ciò che non attraversa il ponte non può finire in una chat per
     * sbaglio*, ed è giusto per l'email e le note. Per il livello è diventato
     * falso il giorno in cui il documento ha chiesto al bot di dirlo: un dato che
     * serve al socio non è un dato che gli si nasconde.
     * ⛔ Restano fuori email, note, storico — e il borsellino ha una **porta sua**
     * (`/players/:id/wallet`), perché chiederlo sia un gesto esplicito e non
     * qualcosa che arriva addosso a ogni identificazione.
     */
    level: z.number().nullable(),
    levelLabel: z.string().nullable(),
    /**
     * 🆕 28/09/2026 (Partite Aperte, mockup approvato, scelta 1A): il test del
     * livello vale **fatto** se c'è un livello nella scheda **oppure** una prova
     * consegnata — anche se aspetta il maestro o la Segreteria. Finché è falso,
     * il bot chiede il test a ogni gesto. La regola sta qui, non nel bot.
     */
    levelTestDone: z.boolean(),
    /** 🆕 Il ruolo scelto sul bot; `null` ⇒ non l'ha ancora detto. ⇒ `RUOLI_NEL_BOT`. */
    role: z.enum(RUOLI_NEL_BOT).nullable(),
});
// ── Cosa ho prenotato, e con chi ────────────────────────────────────────────
/**
 * Una persona in campo, vista dal ponte.
 *
 * ⚠️ `memberId` nullo vuol dire **ospite**, e non è un dato mancante: è una
 * persona che il circolo non conosce per nome e cognome, a cui nessuno può
 * scrivere. Chi la tratta come un socio senza scheda le manderà messaggi che
 * non arrivano da nessuna parte.
 */
export const giocatoreDelPonteSchema = z.object({
    /**
     * 🆕 **La RIGA in campo**, che è ciò che `players/remove` vuole.
     *
     * 🚨 Mancava, e senza di lei «togli» dal bot non si poteva scrivere: la rotta
     * chiede la riga (non il nome, non la scheda — un ospite non ha scheda, e due
     * ospiti si possono chiamare uguale) e la schermata non la dava. Un bottone
     * che non si può riempire è un contratto che promette una porta murata.
     */
    playerId: z.uuid(),
    memberId: z.uuid().nullable(),
    displayName: z.string(),
    guest: z.boolean(),
    /** Il campo è a nome suo. Al più uno per partita, e può non esserci. */
    organizer: z.boolean(),
});
/**
 * ⭐⭐ **I BOTTONI DELLA SCHERMATA DELLA PARTITA, DECISI QUI.**
 *
 * 🧭 È «il gestionale sa, il bot dice» preso sul serio: il bot non legge il
 * roster per dedurre chi può togliere chi — lo chiede. Le regole sono sei, si
 * incrociano, e cambiano: il giorno in cui «dentro le ore della disdetta»
 * cambia verso, se la regola vivesse nel bot bisognerebbe rilasciare il bot, e
 * fino ad allora il bot offrirebbe un bottone che il gestionale rifiuta.
 * 📌 *Un bottone che si mostra secondo una regola e si esegue secondo un'altra
 *    è un vicolo cieco con l'aria di una strada.*
 *
 * ⛔ E un «non lo so» non dà mai un potere: senza `organizerMemberId` i poteri
 * che agiscono **sugli altri** restano falsi per tutti.
 */
export const poteriDelPonteSchema = z.object({
    inMatch: z.boolean(),
    isOrganizer: z.boolean(),
    /**
     * ⭐ Gli manca il livello, e qui gli servirebbe. ⇒ Il bot offre il test.
     * ⚠️ **Non è `canInvite: false` con un'altra faccia**: l'offerta del test
     * arriva *«anche se quella partita è piena, perché il livello gli servirà
     * comunque»*. A campo pieno il bottone dell'invito sparisce e il test resta.
     */
    requiresLevel: z.boolean(),
    capacity: z.number().int(),
    freeSeats: z.number().int(),
    started: z.boolean(),
    /**
     * Dentro le ore della disdetta la partita **non si congela**: il responsabile
     * resta, gli altri escono lo stesso. Cambia una cosa, e va detta al socio: il
     * campo si paga per intero.
     */
    withinCancellationWindow: z.boolean(),
    canInvite: z.boolean(),
    canOpen: z.boolean(),
    canClose: z.boolean(),
    canRemovePlayers: z.boolean(),
    canLeave: z.boolean(),
    canCancel: z.boolean(),
    /** 🆕 26/09/2026 (fase 3, R5) — «🔁 Cambia organizzatore»: solo chi l'ha organizzata, prima che cominci. */
    canChangeOrganizer: z.boolean().default(false),
    /**
     * Perché non si fa niente, in italiano e per un socio. Nullo quando i poteri
     * ci sono: un motivo scritto accanto a un bottone vivo, prima o poi, qualcuno
     * lo mostra.
     */
    blockedReason: z.string().nullable(),
});
export const partitaDelSocioSchema = z.object({
    bookingId: z.uuid(),
    courtName: z.string(),
    date: dataSchema,
    time: oraSchema,
    /** 🆕 A che ora finisce: il bot dice la fascia, e non fa conti sulle durate. */
    endTime: oraSchema,
    durationMinutes: z.number().int(),
    type: z.string(),
    instructorName: z.string().nullable(),
    /** 🆕 In vetrina fra le partite aperte, sì o no — come sulla schermata della partita. */
    openToOthers: z.boolean(),
    /**
     * 🔄⭐ **L'elenco ordinato di chi è in campo, con la sua SCHEDA** — e fino al
     * 22/09/2026 qui c'erano i soli nomi.
     *
     * 🚨 **Il difetto che questo cambio toglie, e che era già scritto nel bot**:
     * dopo un'uscita riuscita il bot deve avvisare *chi è rimasto in partita*. Coi
     * soli nomi l'unica strada sarebbe cercare la scheda **per nome** — cioè
     * indovinare — e su due omonimi si indovina la persona sbagliata: l'avviso
     * arriverebbe a qualcuno che sta ancora giocando.
     * 📌 *Un nome non è un identificativo: è un modo di chiamare più persone.*
     */
    players: z.array(giocatoreDelPonteSchema),
    /** Chi l'ha organizzata. **Nullo quando il circolo non lo dichiara.** */
    organizerMemberId: z.uuid().nullable(),
    /** Cosa può farci **il socio che ha chiesto l'elenco**, e nient'altro. */
    powers: poteriDelPonteSchema,
    /** Quanto deve **questo** socio, in centesimi. Gli altri non lo riguardano. */
    amountDueCents: z.number().int(),
    /** 🌙 La caparra, se la partita ne ha una. */
    deposit: caparraDelPonteSchema.nullable().optional(),
});
export const partiteDelSocioSchema = z.object({ items: z.array(partitaDelSocioSchema) });
export const domandaPartiteSchema = z.object({
    from: dataSchema.optional(),
    to: dataSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
// ── Gli slot liberi ─────────────────────────────────────────────────────────
export const domandaSlotSchema = z.object({
    date: dataSchema,
    durationMinutes: z.coerce.number().int().min(30).max(240).default(90),
    courtId: z.uuid().optional(),
});
/**
 * ⭐⭐ UNO SLOT OFFERTO AL SOCIO PORTA IL SUO PREZZO, e non è un di più.
 *
 * 🗣️ Sua, 18/09/2026: *«come pensi che un giocatore prenoti un campo se non
 * conosce il prezzo? Magari poi si trova un prezzo differente da quello che ha
 * pensato»*.
 *
 * ⚖️ ⇒ Il prezzo fa parte dell'**offerta**, non è una pratica da sistemare
 * dopo: un'ora che il listino non sa quotare **non si offre**, invece di
 * offrirla e scoprire il prezzo a cose fatte. Per questo i due campi non sono
 * `nullable`: se non c'è il prezzo, non c'è lo slot.
 * 📌 *Un'offerta senza prezzo non è un'offerta: è una richiesta di fiducia.*
 */
export const slotDelPonteSchema = z.object({
    courtId: z.uuid(),
    courtName: z.string(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int(),
    /** 💶 Quanto paga CHI PRENOTA, in centesimi. Zero vuol dire gratis davvero. */
    perPlayerCents: z.number().int().min(0),
    /** 💶 E quanto costa il campo intero, per chi chiede «quanto viene in tutto?». */
    courtCents: z.number().int().min(0),
});
export const slotLiberiDelPonteSchema = z.object({ items: z.array(slotDelPonteSchema) });
// ── Prenotare e disdire ─────────────────────────────────────────────────────
export const prenotaDalPonteSchema = z.object({
    memberId: z.uuid(),
    courtId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int().min(30).max(240),
    /**
     * Chi altro viene. Nomi liberi: il bot **non risolve** un nome in un socio —
     * un nome che non si riconosce resta un ospite, e non si inventa nessuno.
     */
    guests: z.array(z.string().trim().min(1).max(80)).max(3).default([]),
    /**
     * 🔑 La stessa chiave mandata due volte è **lo stesso gesto**, non due
     * partite: una rete che cade dopo la scrittura fa riprovare il bot, e senza
     * questa chiave il campo risulterebbe occupato due volte.
     */
    idempotencyKey: z.string().trim().min(8).max(200),
    /**
     * 🌙 Il socio ha letto che la caparra non sarà rimborsabile (mancano meno
     * delle ore della disdetta) e ha confermato. Senza, il gestionale rifiuta.
     */
    acceptNonRefundable: z.boolean().default(false),
});
export const disdiciDalPonteSchema = z.object({
    memberId: z.uuid(),
    reason: z.string().trim().max(200).nullable().default(null),
});
/**
 * La risposta di una scrittura chiesta dal bot.
 *
 * ⛔ `reason` è **per il socio**: non ci passa mai un codice interno, un nome di
 * tabella, un percorso di file. La guardia è la stessa dell'§ 02.0 e fallisce
 * chiusa — davanti a un errore che non sa tradurre non ritaglia il pezzo
 * sospetto: sostituisce tutto il dettaglio.
 */
export const esitoDalPonteSchema = z.object({
    outcome: verdettoDelPonteSchema,
    reason: z.string().nullable(),
    bookingId: z.uuid().nullable(),
    /** 🌙 La caparra della prenotazione, quando ne ha una: da pagare, o com'è finita. */
    deposit: caparraDelPonteSchema.nullable().optional(),
});
// ── ⏱️ Arrivo in ritardo (27/09/2026, mockup «Scrivi alla Segreteria» ④) ───
/**
 * Il socio dice all'assistente che arriva in ritardo, e il gestionale avvisa i
 * compagni di QUELLA partita. Una volta per partita, e solo intorno all'ora:
 * da due ore prima dell'inizio a mezz'ora dopo.
 * 🔑 `idempotencyKey` come le altre scritture del ponte.
 */
export const ritardoDalPonteSchema = z.object({
    minutes: z.number().int().min(1).max(120).optional(),
    idempotencyKey: z.string().min(8).max(100),
});
/**
 * ⚖️ Chi è stato messo in coda, non chi l'ha ricevuto: se un compagno ha
 * l'assistente lo sa il BOT, che tiene le sue chat. È lui che dice al socio
 * chi è stato avvisato e chi no.
 */
export const esitoRitardoSchema = z.object({
    /** ⚖️ I due verdetti del ponte, e basta: «già avvisati» è un `fatto` con `already`. */
    outcome: z.enum(['fatto', 'rifiutato']),
    /** L'avviso c'era già (da un'altra richiesta): i compagni lo sanno dall'ora in `at`. */
    already: z.boolean(),
    /** Per il socio, quando è `rifiutato`: cortese, e con la strada giusta. */
    reason: z.string().nullable(),
    /** L'ora in cui i compagni sono stati avvisati («17:52»), quando è `fatto`. */
    at: z.string().nullable(),
    /** I compagni soci della partita, a cui è andato l'avviso. Gli ospiti non ci sono. */
    teammates: z.array(z.object({ memberId: z.uuid(), name: z.string() })),
});
// ── 🔁 Il campo fisso chiesto al bot (27/09/2026) ──────────────────────────
/**
 * 🆕 **Il campo fisso dal bot** (mockup «Fisso dal bot», approvato il
 * 27/09/2026): ogni settimana, stesso giorno, stessa ora, stesso campo, per un
 * certo numero di mesi. Nasce subito, come una prenotazione; di sera con UNA
 * caparra per tutto il fisso (fase 3, R7).
 */
export const domandaFissoDalPonteSchema = z.object({
    memberId: z.uuid(),
    courtId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.coerce.number().int().min(30).max(240),
    months: z.coerce.number().int().min(1).max(6),
    /**
     * 🔁 Voce 47 — il campo fisso che questo rinnova: si parte dalla settimana dopo la sua ultima
     * partita, anche oltre la finestra in cui si prenota di solito. Il gestionale controlla che sia
     * suo, e che giorno, ora, campo e durata siano quelli.
     */
    renewsSeriesId: z.uuid().optional(),
});
/** Prima del tocco: quali settimane ci sono, quali no e perché, quanto costa. Non scrive niente. */
export const anteprimaFissoDalPonteSchema = z.object({
    /** L'ultimo giorno del fisso. */
    until: dataSchema,
    /** Le partite che nascerebbero, la prima compresa. */
    dates: z.array(dataSchema),
    /** Le settimane che non ci sono, con la frase del circolo (un torneo, già prenotato…). */
    skipped: z.array(z.object({ date: dataSchema, reason: z.string() })),
    /** Quanto costa una partita, tutta, e la quota di ciascuno. Dalla prima settimana. */
    priceCents: z.number().int(),
    perPlayerCents: z.number().int(),
    /** 🌙 La caparra, una sola per tutto il fisso. Nulla quando non serve. */
    deposit: z
        .object({
        amountCents: z.number().int(),
        cancelHours: z.number().int(),
        /** 🆕 Voce 51: la prima partita è già dentro le ore della disdetta — quella sera conta come le altre. */
        firstWithinCancelWindow: z.boolean().default(false),
    })
        .nullable(),
    /** 🎁 Il premio a chi organizza, quando è acceso: i crediti per ogni partita giocata. */
    premio: z.object({ creditsPerMatch: z.number().int(), creditValueCents: z.number().int() }).nullable(),
});
export const prenotaFissoDalPonteSchema = domandaFissoDalPonteSchema.extend({
    durationMinutes: z.number().int().min(30).max(240),
    months: z.number().int().min(1).max(6),
    /** 🔑 La stessa chiave è lo stesso fisso: una rete caduta non ne fa due. */
    idempotencyKey: z.string().trim().min(8).max(200),
    acceptNonRefundable: z.boolean().default(false),
});
export const esitoFissoDalPonteSchema = esitoDalPonteSchema.extend({
    seriesId: z.uuid().nullable(),
    /** Quante partite sono nate, e l'ultimo giorno. */
    count: z.number().int(),
    until: dataSchema.nullable(),
});
/**
 * 🔁 **Il rinnovo del campo fisso** (voce 47, mockup approvato il 27/09/2026), riletto al tocco del
 * bottone sotto l'avviso (regola 7 del bot):
 *   · `da_rinnovare` — dal bot (`from: 'bot'`, coi dati per il fisso nuovo) o dalla Segreteria
 *     (`from: 'segreteria'`, col messaggio già scritto);
 *   · `rinnovato` — c'è già il fisso nuovo, fino a `renewedUntil`;
 *   · `finito` — l'ultima partita è già cominciata: si prende un campo fisso nuovo.
 */
export const rinnovoDelFissoSchema = z.object({
    state: z.enum(['da_rinnovare', 'rinnovato', 'finito']),
    from: z.enum(['bot', 'segreteria']),
    /** L'ultima partita del fisso: giorno, ora, campo, durata. */
    lastDate: dataSchema,
    time: oraSchema,
    courtId: z.uuid(),
    courtName: z.string(),
    durationMinutes: z.number().int(),
    /** Il primo giorno del fisso nuovo (una settimana dopo l'ultima). */
    date: dataSchema,
    /** I mesi che il bot può proporre (Impostazioni › Campi fissi dal bot). */
    maxMonths: z.number().int(),
    renewedUntil: dataSchema.nullable(),
    /** ✉️ Per la Segreteria: il messaggio già scritto. */
    requestText: z.string(),
    /** Il socio ha già detto «No, grazie». Non chiude niente: se cambia idea, rinnova lo stesso. */
    declined: z.boolean(),
});
/** «No, grazie»: segnato, perché la Segreteria sappia che dopo l'ultima partita quel campo è libero. */
export const esitoNoAlRinnovoSchema = z.object({
    outcome: verdettoDelPonteSchema,
    reason: z.string().nullable(),
    lastDate: dataSchema.nullable(),
});
// ── La partita, e chi ci sta dentro ─────────────────────────────────────────
/**
 * La schermata della partita, in una risposta sola.
 *
 * ⚖️ **Perché una rotta apposta e non «l'elenco, filtrato»**: prima di eseguire
 * un gesto il bot **rilegge la partita**, e deve rileggere *quella* — non
 * ripescare trenta prenotazioni per tenerne una. Dal documento: *«se nel
 * frattempo è cambiata, non esegue il sì detto a una situazione diversa».*
 */
export const schedaPartitaDelPonteSchema = z.object({
    bookingId: z.uuid(),
    courtName: z.string(),
    date: dataSchema,
    time: oraSchema,
    endTime: oraSchema,
    durationMinutes: z.number().int(),
    type: z.string(),
    status: z.string(),
    instructorName: z.string().nullable(),
    players: z.array(giocatoreDelPonteSchema),
    organizerMemberId: z.uuid().nullable(),
    /** In vetrina fra le partite aperte, sì o no. */
    openToOthers: z.boolean(),
    /** Quanto deve **il socio che chiede**, in centesimi. Gli altri non lo riguardano. */
    amountDueCents: z.number().int(),
    powers: poteriDelPonteSchema,
    /** 🌙 La caparra della fascia serale, se la partita ne ha una. */
    deposit: caparraDelPonteSchema.nullable().optional(),
});
export const chiChiedeSchema = z.object({ memberId: z.uuid() });
/**
 * ⭐⭐ **«COM'È FINITA?» — la domanda che chiude il terzo verdetto.**
 *
 * 🧭 Su un `non_lo_so` il bot non afferma e **vieta di rifare** il gesto. Ma
 * qualcuno deve pur sapere com'è andata, e non può essere il socio a scoprirlo
 * al campo. ⇒ Con la stessa chiave di idempotenza con cui aveva chiesto, il bot
 * richiede: o esce la partita, e allora era riuscita, o non esce niente, e
 * allora non c'è.
 * 📌 *Un verdetto ignoto non si risolve indovinando: si risolve richiedendo al
 *    solo che lo sa.*
 */
export const domandaPerChiaveSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(200),
    memberId: z.uuid(),
});
export const rispostaPerChiaveSchema = z.object({
    /** Nulla quando di quel gesto non c'è traccia: **non è un guasto**. */
    booking: schedaPartitaDelPonteSchema.nullable(),
});
/**
 * Chi si toglie o si aggiunge.
 *
 * ⚠️ **Un giocatore si indica con la sua RIGA (`playerId`), non col nome.** Due
 * ospiti si possono chiamare uguale, e un socio e un ospite pure: togliere «per
 * nome» vuol dire, prima o poi, togliere la persona sbagliata da un campo.
 */
export const togliDalPonteSchema = chiChiedeSchema.extend({ playerId: z.uuid() });
/**
 * Chi entra: **o** un socio (`addMemberId`) **o** un ospite (`guestName`).
 * Mai tutti e due: un nome accanto a un id è una domanda a cui risponde
 * l'ordine degli `if`, e l'ordine degli `if` non lo ha deciso nessuno.
 */
export const aggiungiDalPonteSchema = chiChiedeSchema
    .extend({
    addMemberId: z.uuid().optional(),
    guestName: z.string().trim().min(1).max(80).optional(),
})
    .refine((d) => Boolean(d.addMemberId) !== Boolean(d.guestName), {
    message: 'Serve il socio oppure il nome dell’ospite, uno solo dei due.',
});
/**
 * L'esito di un cambio di formazione.
 *
 * ⭐⭐ **PORTA LA PARTITA COM'È RIMASTA**, non due numeri da cui dedurla, e le
 * ragioni sono due.
 *
 * ① *Il tocco si rilegge, sempre.* Dopo un gesto il bot deve comunque
 *    rileggere la partita per ridisegnare i bottoni: mandargliela qui dentro
 *    toglie un giro e, soprattutto, toglie la finestra in cui i due potrebbero
 *    raccontare due formazioni diverse.
 * ② **Chi è rimasto in partita va avvisato**, e per scrivergli serve la sua
 *    scheda. Con i soli numeri il bot dovrebbe ricostruirla per nome, cioè
 *    indovinare — e su due omonimi indovina la persona sbagliata.
 *
 * ⚠️ È **nulla** quando il verdetto è `non_lo_so`: là non si sa nemmeno se il
 * gesto è passato, e mandare una formazione sarebbe affermare qualcosa.
 */
export const esitoSullaPartitaSchema = z.object({
    outcome: verdettoDelPonteSchema,
    reason: z.string().nullable(),
    booking: schedaPartitaDelPonteSchema.nullable(),
});
export const esitoFormazioneSchema = esitoSullaPartitaSchema.extend({
    /** Chi è entrato o chi è uscito: la sua scheda, per poterglielo dire. */
    player: giocatoreDelPonteSchema.nullable(),
});
// ── Le chiavi del ponte, come le gestisce la segreteria ─────────────────────
export const chiaveInElencoSchema = z.object({
    id: z.uuid(),
    nome: z.string(),
    /** `pmo_a91c4f2e8b07_…` — riconoscibile, inutilizzabile. */
    mostrata: z.string(),
    creataIl: z.string(),
    ultimoUsoIl: z.string().nullable(),
    revocataIl: z.string().nullable(),
});
export const elencoChiaviSchema = z.object({ items: z.array(chiaveInElencoSchema) });
export const nuovaChiaveSchema = z.object({
    /**
     * A cosa serve, in italiano: «bot dei soci — prova».
     *
     * ⭐ Obbligatorio, e non per burocrazia: una chiave senza nome, fra sei mesi,
     * non la revoca nessuno — per paura di spegnere qualcosa.
     */
    nome: z.string().trim().min(3).max(80),
});
export const chiaveAppenaConiataSchema = z.object({
    id: z.uuid(),
    /**
     * ⚠️ **L'unica volta che esiste fuori di qui.** Non si può rileggere: il
     * gestionale ne conserva l'impronta, non la chiave. Chi la perde ne conia
     * un'altra e revoca questa — che è esattamente ciò che un segreto unico non
     * permetteva di fare.
     */
    chiara: z.string(),
});
// ── Le partite aperte ───────────────────────────────────────────────────────
/**
 * Una partita di qualcun altro, vista dalla vetrina.
 *
 * 🔒⭐⭐ **SOLO NUMERI, MAI UN NOME.** Dal documento dei flussi: *«Di una partita
 * che non è sua il socio vede solo i numeri: quanti sono su quattro, quando, di
 * che livello. Mai un nome.»* E la ragione è scritta accanto: *«una vetrina di
 * roster aprirebbe dal retro la porta sui soci che il gruppo privato tiene
 * chiusa»*. ⇒ Qui dentro non c'è nessun campo che possa contenere una persona,
 * e la query che la riempie i nomi non li legge nemmeno.
 */
export const partitaApertaSchema = z.object({
    bookingId: z.uuid(),
    courtName: z.string(),
    date: dataSchema,
    time: oraSchema,
    endTime: oraSchema,
    durationMinutes: z.number().int(),
    players: z.number().int(),
    capacity: z.number().int(),
    freeSeats: z.number().int(),
    /** Il numero del circolo, per chi sa cosa farne. */
    level: z.number().nullable(),
    /**
     * ⭐ E la **parola** con cui il circolo chiama quel livello: ai soci i livelli
     * non si sono mai detti a numeri. Nulla quando il circolo non ha dichiarato
     * nessuna scala — allora il bot non dice nessun livello, invece di tradurlo
     * con una scala inventata da lui.
     */
    levelLabel: z.string().nullable(),
});
export const vetrinaSchema = z.object({
    /** Quelle in cui **questo** socio può entrare: un bottone che non può funzionare non si mostra. */
    items: z.array(partitaApertaSchema),
    /**
     * ⚖️ Quante ce ne sono in tutto, prima dei filtri. Serve a dire «ce ne sono
     * tre, ma nessuna del tuo livello» invece di un elenco vuoto senza
     * spiegazione — che è un vicolo cieco con l'aria di una vetrina vuota.
     */
    total: z.number().int(),
    /**
     * Il socio non ha un livello dichiarato. ⇒ Il bot offre il test: *«senza
     * livello non si organizza una partita e non si invita nessuno»*.
     */
    requiresLevel: z.boolean(),
});
// ── Le consultazioni: quello che il socio legge e basta ─────────────────────
/**
 * 💶 Il borsellino.
 *
 * 🗣️ Dal documento: *«un saldo a zero è una risposta normale: non è un errore,
 * non è un borsellino "non attivo", e non va commentato. Nessuna data di
 * aggiornamento del saldo.»* ⇒ Qui non c'è nessun `updatedAt` e nessun campo
 * «attivo»: i due dati che il bot non deve avere, per non essere tentato di
 * raccontarli.
 */
export const borsellinoDelPonteSchema = z.object({
    memberId: z.uuid(),
    balanceCents: z.number().int(),
    /**
     * 🌙 Il credito che scade (dalle caparre), per data di scadenza. Vuoto ⇒ il
     * bot non dice niente di scadenze.
     */
    credits: z.array(creditoInScadenzaSchema).optional(),
    /**
     * 🎁 I crediti premio di chi organizza (voce 33, 26/09/2026). Assente ⇒ il
     * premio è spento e il socio non ha crediti: il bot non ne parla.
     * ⚖️ Non sono euro del borsellino: il bot li dice a parte, e dice che non si
     * prelevano e non si rimborsano.
     */
    premio: z
        .object({
        creditValueCents: z.number().int(),
        creditsPerMatch: z.number().int(),
        months: z.number().int(),
        available: z.number().int(),
        incoming: z.array(z.object({ date: dataSchema, time: oraSchema, courtName: z.string(), credits: z.number().int() })),
        expiring: z.array(z.object({ credits: z.number().int(), expiresOn: dataSchema })),
        /**
         * 🆕 27/09/2026 (voce 46, «Borsellino e Crediti») — l'altra partita entro
         * quanti giorni, per la spiegazione: il bot non lo scrive a memoria.
         */
        windowDays: z.number().int().optional(),
    })
        .optional(),
    /**
     * 🆕 27/09/2026 (voce 46) — l'acconto della sera, per la spiegazione dei
     * Crediti: quanti mesi valgono quelli che tornano da un acconto, e quanti
     * giorni prima della scadenza arriva l'avviso. Assente ⇒ l'acconto è spento
     * e il socio non ha Crediti da un acconto: il bot non ne parla.
     */
    deposit: z.object({ creditMonths: z.number().int(), reminderDays: z.number().int() }).optional(),
});
/**
 * La scheda di una persona del gruppo del socio.
 *
 * ⚠️ **Non è una ricerca**: si chiede per id, e gli id il bot li ha perché
 * quelle persone le ha fatte entrare lui col proprio invito. Una ricerca per
 * nome sui quasi tremila soci del circolo, da una chat, sarebbe la rubrica del
 * circolo in mano a chiunque.
 */
/**
 * Il socio sceglie come l'assistente lo chiama (24/09/2026). `null` ⇒ lo
 * chiama per nome.
 */
export const soprannomeDelPonteSchema = z.object({ nickname: soprannomeSchema.nullable() });
/**
 * ⚖️ Esce col verdetto come le altre scritture del ponte: un soprannome che
 * non va bene è un `rifiutato` con la frase per il socio, non un errore.
 */
export const esitoSoprannomeSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato']),
    reason: z.string().nullable(),
    nickname: z.string().nullable(),
});
export const personaDelPonteSchema = z.object({
    memberId: z.uuid(),
    displayName: z.string(),
    level: z.number().nullable(),
    levelLabel: z.string().nullable(),
    /** È ancora un cliente del circolo? Chi non lo è non entra in campo. */
    active: z.boolean(),
});
export const personeDelPonteSchema = z.object({ items: z.array(personaDelPonteSchema) });
/**
 * ⭐⭐ **QUELLO CHE IL CIRCOLO DICHIARA DI SÉ** — e il bot non inventa.
 *
 * 🗣️ Dal documento: *«Il numero non è scritto dentro il bot: viene dal circolo»*
 * e *«se il circolo non ha la risposta, il bot lo dice e manda in segreteria —
 * mai un'invenzione»*. Un numero plausibile ma inventato fa chiamare un
 * estraneo: è il modo peggiore di sbagliare, ed è già successo.
 *
 * ⛔ Ogni voce è **nullabile**, e nulla vuol dire «non lo dichiariamo»: è un
 * caso che il bot sa raccontare («non riesco a recuperarlo»), mentre un valore
 * di ripiego non si distingue da uno vero.
 */
export const conoscenzaDelPonteSchema = z.object({
    /**
     * 🕰️ **Oggi, secondo il circolo.** Il bot non indovina mai il giorno della
     * settimana né la data: dentro un container il fuso è UTC, e «oggi» calcolato
     * lì sbaglia di un giorno per due ore ogni notte.
     */
    today: dataSchema,
    secretariatPhone: z.string().nullable(),
    secretariatHours: z.string().nullable(),
    /**
     * 💬 Il numero su cui la segreteria legge WhatsApp, in forma internazionale
     * (`+393331234567`). Nullo = niente bottone WhatsApp: il circolo non l'ha
     * dichiarato, o l'ha scritto senza prefisso del paese e non si indovina.
     */
    secretariatWhatsapp: z.string().nullable(),
    privacyUrl: z.string().nullable(),
    /** Fin quando si prenota. Oltre, non si elencano campi liberi. */
    bookingWindowDays: z.number().nullable(),
    cancellationHours: z.number().nullable(),
    /** Le parole della scala, in ordine: servono a spiegare il test, non a tradurre. */
    levelWords: z.array(z.string()),
    /**
     * 🆕 26/09/2026 — gli avvisi all'organizzatore sulla partita (§ 4.4 del
     * documento della caparra): il giorno prima a quest'ora se è incompleta, il
     * giorno stesso tante ore prima. Li scrive la Segreteria in Impostazioni ›
     * Caparra della sera; valgono per tutte le partite.
     * 🔄 `earliestAt` (26/09/2026): nessun avviso all'organizzatore prima di
     * quest'ora — «Mai prima delle», che decide il circolo (prima era il bot, alle 8).
     */
    matchNotices: z
        .object({ dayBeforeAt: oraSchema, hoursBefore: z.number().int(), earliestAt: oraSchema })
        .optional(),
    /**
     * 🔁 27/09/2026 — il campo fisso si può chiedere al bot, fino a quanti mesi.
     * Nullo (o assente) = spento: il bot non mostra il bottone.
     *
     * 🎁 `reward` (27/09/2026): il premio a chi organizza è acceso. Il bottone del
     * bot dice «🎁 Giovedì fisso? Scopri i vantaggi» solo allora; spento, «🔁».
     */
    recurring: z.object({ maxMonths: z.number().int(), reward: z.boolean().optional() }).nullable().optional(),
    /**
     * 📚 Le informazioni sul circolo che il circolo ha scritto — dove siamo, i
     * campi, cosa si trova, come si paga, prenotare e disdire — nell'ordine dei
     * bottoni, e solo quelle che hanno un testo.
     *
     * 🔒⭐ **Solo il testo per il socio.** Gli appunti di chi le mantiene restano
     * nel gestionale: il documento dei flussi vuole che *«le annotazioni di chi
     * la mantiene restino fuori»*, e il posto più sicuro per tenerle fuori è non
     * metterle nel contratto.
     * ⚠️ Un argomento assente non è un guasto: il circolo non l'ha scritto, e il
     * bot dice che non riesce a recuperarlo e manda in segreteria.
     */
    topics: z.array(z.object({ key: z.string(), title: z.string(), text: z.string() })),
});
// ── Gli orari e i campi liberi ──────────────────────────────────────────────
/**
 * ⭐⭐ **DUE REGOLE, e sono del documento dei flussi (21/09/2026).**
 *
 * ① *«Oltre la finestra di prenotazione non si elencano campi liberi»* —
 *    sarebbe mandare il socio a guardare qualcosa che non può prenotare. ⇒ La
 *    risposta non è un errore: è un giorno **non prenotabile**, con scritto
 *    fino a quando si prenota, così il bot lo dice e manda in segreteria per le
 *    date lontane e per i campi fissi.
 * ② *«Le fasce già cominciate di oggi non si mostrano»* — e a dire che ora è
 *    non può essere il bot: dentro un container il fuso è UTC.
 *
 * 📌 *Un elenco vuoto senza il perché è un vicolo cieco; lo stesso elenco con
 *    accanto «si prenota fino al …» è una risposta.*
 */
export const finestraSchema = z.object({
    date: dataSchema,
    today: dataSchema,
    /** L'ultimo giorno prenotabile. Nullo = il circolo non dichiara nessun limite. */
    bookableUntil: dataSchema.nullable(),
    /** Questo giorno si può prenotare? Se no, l'elenco è vuoto **per una ragione**. */
    bookable: z.boolean(),
});
export const campoLiberoSchema = z.object({
    courtId: z.uuid(),
    courtName: z.string(),
    perPlayerCents: z.number().int().min(0),
    courtCents: z.number().int().min(0),
});
export const fasciaDelGiornoSchema = z.object({
    time: oraSchema,
    endTime: oraSchema,
    /** I campi liberi, ognuno col suo prezzo: due campi possono costare diverso. */
    freeCourts: z.array(campoLiberoSchema),
    /**
     * Quanti campi, a quell'ora e **per quella durata**, ci sarebbero ma non si
     * possono prendere: occupati da una partita, o chiusi. ⇒ «tutto pieno» è una
     * risposta diversa da «a quell'ora non si gioca», e il socio le distingue:
     * la prima la riprova domani.
     * ⚠️ Le ore in cui quella durata **non ci sta** (le 22:30 per 90 minuti, con
     * la griglia che finisce alle 23:00) non compaiono affatto: dire «due campi
     * occupati» di due campi liberi sarebbe una risposta falsa.
     */
    busyCourts: z.number().int(),
});
export const giornoDiFasceSchema = finestraSchema.extend({
    durationMinutes: z.number().int(),
    slots: z.array(fasciaDelGiornoSchema),
});
export const slotLiberiDelGiornoSchema = finestraSchema.extend({
    items: z.array(slotDelPonteSchema),
});
// ── Il test di livello ──────────────────────────────────────────────────────
/**
 * Una prova consegnata, come la racconta il bot.
 *
 * 🗣️ Dal documento dei flussi: *«Il livello non è una curiosità: senza livello
 * non si organizza una partita e non si invita nessuno»*. ⇒ Questa scheda è
 * ciò che permette al bot di dire **com'è andata** e **cosa può fare adesso**,
 * senza calcolare niente per conto suo.
 */
export const provaDelTestSchema = z.object({
    token: z.string(),
    submittedAt: z.string(),
    /** `pass` · `fail` · `skip`. Il terzo è di chi il quiz non ce l'ha. */
    outcome: z.string(),
    correct: z.number().int(),
    total: z.number().int(),
    /** Il livello che le risposte dicono. */
    demonstratedLevel: z.number().nullable(),
    demonstratedLabel: z.string().nullable(),
    /** Quello che il socio ha in scheda adesso. */
    levelOnFile: z.number().nullable(),
    levelOnFileLabel: z.string().nullable(),
    /** 📉 Il test dice meno di quello che ha in scheda, e di una fascia intera. */
    testSaysLess: z.boolean(),
    /**
     * ⏳ Ha dimostrato sopra il tetto: il livello lo certifica il maestro,
     * guardandolo giocare. `coachMessage` è la frase **già scritta dal circolo**:
     * il bot la ripete, non la riscrive.
     */
    waitingForCoach: z.boolean(),
    coachMessage: z.string().nullable(),
    /**
     * ⛔ Il gradino **offerto**, mai imposto: un livello più basso non lo si dà a
     * nessuno, glielo si può solo offrire. Nullo quando non c'è niente da
     * offrire (uguale non è una scelta, più alto è un regalo).
     */
    offeredStep: z.string().nullable(),
    offeredStepLevel: z.number().nullable(),
    /** Può ancora scegliere: «mi fermo», «riprovo», o il gradino. */
    canChoose: z.boolean(),
    choice: z.string(),
    applied: z.boolean(),
    appliedLevel: z.number().nullable(),
});
export const statoDelTestSchema = z.object({
    memberId: z.uuid(),
    level: z.number().nullable(),
    levelLabel: z.string().nullable(),
    /** Può fare una prova adesso. */
    admitted: z.boolean(),
    /** Quale prova del giro sta per fare: 1, 2 o 3. */
    attempt: z.number().int(),
    attemptsPerRound: z.number().int(),
    /**
     * ⚠️ Quante domande sono in tutto. Prima che il socio dichiari una fascia è
     * una **previsione**: il bot lo dice, invece di promettere un numero che poi
     * cambia.
     */
    questionsTotal: z.number().int(),
    /**
     * 🔑 Il gettone del giro. ⛔ **Non è un indirizzo da aprire**: il test si fa
     * in chat, una domanda per volta. Il bot lo tiene e non lo mostra mai.
     */
    token: z.string().nullable(),
    /** L'attesa fra un giro e l'altro. Oggi è a zero giorni: resta nel contratto. */
    waiting: z.object({ reason: z.string(), from: z.string(), days: z.number().int() }).nullable(),
    lastCard: provaDelTestSchema.nullable(),
    /** 🔔 Il promemoria a chi il livello non ce l'ha: dovuto adesso, sì o no. */
    reminder: z.object({ due: z.boolean(), reason: z.string(), period: z.string() }),
});
export const passoDelTestSchema = z.object({
    finished: z.boolean(),
    number: z.number().int(),
    total: z.number().int(),
    totalCertain: z.boolean(),
    /**
     * ⛔ Le opzioni portano **solo il testo**: il bot risponde con la POSIZIONE
     * del bottone. Mandargli un valore da rimandare indietro vorrebbe dire
     * lasciargli scegliere una risposta che non è fra quelle mostrate.
     */
    question: z.object({ key: z.string(), text: z.string(), options: z.array(z.string()) }).nullable(),
    /** La scheda appena consegnata, quando il giro finisce con questa risposta. */
    card: provaDelTestSchema.nullable(),
});
export const rispostaAlTestSchema = z.object({
    /** La domanda a cui si risponde: se non è quella corrente, la risposta si rifiuta. */
    key: z.string().trim().min(1),
    /** La POSIZIONE del bottone toccato. ⚠️ Zero è una risposta, «niente» no. */
    index: z.number().int().min(0),
});
export const sceltaDelTestSchema = z.object({
    token: z.string().trim().min(1),
    choice: z.enum(['mi_fermo', 'riprovo', 'scendo']),
});
export const sceltaRegistrataSchema = z.object({
    choice: z.string(),
    /** Il livello che ne è uscito, se ne è uscito uno. */
    level: z.number().nullable(),
    levelLabel: z.string().nullable(),
    applied: z.boolean(),
    reason: z.string(),
});
//# sourceMappingURL=ponte.js.map